/**
 * Enhanced currency utilities for user-specific language and locale formatting
 * Handles currency formatting based on user's language preferences
 */

/**
 * Get locale string based on user language
 * @param {string} userLanguage - User's language code
 * @returns {string} Locale string for Intl.NumberFormat
 */
function getLocaleString(userLanguage) {
    const localeMap = {
        'cs': 'cs-CZ',    // Czech
        'da': 'da-DK',    // Danish
        'nl': 'nl-NL',    // Dutch
        'en': 'en-GB',    // English (GB)
        'fr': 'fr-FR',    // French
        'de': 'de-DE',    // German
        'it': 'it-IT',    // Italian
        'pl': 'pl-PL',    // Polish
        'pt': 'pt-PT',    // Portuguese
        'es': 'es-ES'     // Spanish
    };
    
    return localeMap[userLanguage] || 'en-GB';
}

/**
 * Get currency symbol for a given currency code
 * @param {string} currencyCode - The currency code (e.g., 'EUR', 'USD')
 * @returns {string} Currency symbol
 */
export function getCurrencySymbol(currencyCode) {
    const currencySymbols = {
        'EUR': '€',
        'USD': '$',
        'GBP': '£',
        'JPY': '¥',
        'CNY': '¥',
        'INR': '₹',
        'RUB': '₽',
        'KRW': '₩',
        'TRY': '₺',
        'PLN': 'zł',
        'CHF': 'CHF',
        'SEK': 'kr',
        'NOK': 'kr',
        'DKK': 'kr',
        'CZK': 'Kč',
        'HUF': 'Ft',
        'AUD': 'A$',
        'CAD': 'C$',
        'NZD': 'NZ$',
        'ZAR': 'R',
        'BRL': 'R$',
        'MXN': '$',
        'AED': 'د.إ',
        'SAR': '﷼',
    };
    
    return currencySymbols[currencyCode] || currencyCode;
}

/**
 * Formats a monetary amount according to user's locale preferences
 * @param {number|string} amount - The amount to format
 * @param {string} currencyCode - The currency code (e.g., 'EUR', 'USD')
 * @param {string} userLanguage - User's language code
 * @param {object} options - Additional formatting options
 * @returns {string} Formatted currency string
 */
export function formatCurrencyForUser(amount, currencyCode = 'EUR', userLanguage = 'en', options = {}) {
    if (amount === null || amount === undefined || amount === '') {
        return '';
    }

    try {
        const numValue = Number(amount);
        if (!isFinite(numValue)) {
            return '0,00 €'; // Fallback for invalid amounts
        }

        const locale = getLocaleString(userLanguage);
        const defaultOptions = {
            style: 'currency',
            currency: currencyCode,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        };

        const formatOptions = { ...defaultOptions, ...options };

        return new Intl.NumberFormat(locale, formatOptions).format(numValue);

    } catch (error) {
        console.error('Error formatting currency for user:', error);
        // Fallback formatting
        return `${amount} ${getCurrencySymbol(currencyCode)}`;
    }
}

/**
 * Formats a number (without currency symbol) according to user's locale
 * @param {number|string} amount - The amount to format
 * @param {string} userLanguage - User's language code
 * @param {object} options - Additional formatting options
 * @returns {string} Formatted number string
 */
export function formatNumberForUser(amount, userLanguage = 'en', options = {}) {
    if (amount === null || amount === undefined || amount === '') {
        return '';
    }

    try {
        const numValue = Number(amount);
        if (!isFinite(numValue)) {
            return '0';
        }

        const locale = getLocaleString(userLanguage);
        const defaultOptions = {
            style: 'decimal',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        };

        const formatOptions = { ...defaultOptions, ...options };

        return new Intl.NumberFormat(locale, formatOptions).format(numValue);

    } catch (error) {
        console.error('Error formatting number for user:', error);
        return String(amount);
    }
}

/**
 * Formats a currency amount with custom symbol placement and formatting
 * @param {number|string} amount - The amount to format
 * @param {string} currencyCode - The currency code
 * @param {string} userLanguage - User's language code
 * @param {boolean} symbolFirst - Whether to place symbol before amount (default: false for most European locales)
 * @returns {string} Formatted currency string with custom symbol placement
 */
export function formatCurrencyWithCustomSymbol(amount, currencyCode = 'EUR', userLanguage = 'en', symbolFirst = false) {
    if (amount === null || amount === undefined || amount === '') {
        return '';
    }

    try {
        const numValue = Number(amount);
        if (!isFinite(numValue)) {
            return symbolFirst ? `${getCurrencySymbol(currencyCode)} 0,00` : `0,00 ${getCurrencySymbol(currencyCode)}`;
        }

        const formattedNumber = formatNumberForUser(amount, userLanguage);
        const symbol = getCurrencySymbol(currencyCode);

        return symbolFirst ? `${symbol} ${formattedNumber}` : `${formattedNumber} ${symbol}`;

    } catch (error) {
        console.error('Error formatting currency with custom symbol:', error);
        return `${amount} ${getCurrencySymbol(currencyCode)}`;
    }
}

/**
 * Safely formats a price value with proper error handling for user locale
 * @param {any} value - The value to format
 * @param {string} userLanguage - User's language code
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} Formatted price string
 */
export function safeFormatPriceForUser(value, userLanguage = 'en', decimals = 2) {
    try {
        if (value === null || value === undefined || value === '' || isNaN(value)) {
            return formatNumberForUser(0, userLanguage, { 
                minimumFractionDigits: decimals, 
                maximumFractionDigits: decimals 
            });
        }
        
        const numValue = Number(value);
        if (!isFinite(numValue)) {
            return formatNumberForUser(0, userLanguage, { 
                minimumFractionDigits: decimals, 
                maximumFractionDigits: decimals 
            });
        }
        
        return formatNumberForUser(numValue, userLanguage, { 
            minimumFractionDigits: decimals, 
            maximumFractionDigits: decimals 
        });
    } catch (error) {
        console.warn('Error formatting price for user:', error, 'Value:', value);
        return '0,00'; // Fallback
    }
}

/**
 * Safely calculates and formats absolute discount value for user locale
 * @param {any} discount - The discount value (can be negative)
 * @param {string} userLanguage - User's language code
 * @returns {string} Formatted absolute discount
 */
export function safeCalculateDiscountForUser(discount, userLanguage = 'en') {
    try {
        if (discount === null || discount === undefined || discount === '' || isNaN(discount)) {
            return formatNumberForUser(0, userLanguage);
        }
        
        const numDiscount = Number(discount);
        if (!isFinite(numDiscount)) {
            return formatNumberForUser(0, userLanguage);
        }
        
        return formatNumberForUser(Math.abs(numDiscount), userLanguage);
    } catch (error) {
        console.warn('Error calculating discount for user:', error, 'Discount:', discount);
        return formatNumberForUser(0, userLanguage);
    }
}

/**
 * Safely calculates and formats final amount after discount for user locale
 * @param {any} amountPreDiscount - The amount before discount
 * @param {any} discount - The discount amount (usually negative)
 * @param {string} userLanguage - User's language code
 * @returns {string} Formatted final amount
 */
export function safeCalculateFinalAmountForUser(amountPreDiscount, discount, userLanguage = 'en') {
    try {
        const preDiscount = (amountPreDiscount === null || amountPreDiscount === undefined || amountPreDiscount === '' || isNaN(amountPreDiscount)) 
            ? 0 : Number(amountPreDiscount);
        const discountAmount = (discount === null || discount === undefined || discount === '' || isNaN(discount)) 
            ? 0 : Number(discount);
        
        if (!isFinite(preDiscount) || !isFinite(discountAmount)) {
            return formatNumberForUser(0, userLanguage);
        }
        
        const finalAmount = preDiscount + discountAmount;
        return isFinite(finalAmount) ? formatNumberForUser(finalAmount, userLanguage) : formatNumberForUser(0, userLanguage);
    } catch (error) {
        console.warn('Error calculating final amount for user:', error, 'PreDiscount:', amountPreDiscount, 'Discount:', discount);
        return formatNumberForUser(0, userLanguage);
    }
}

/**
 * Safely formats a monetary value with currency symbol for user locale
 * @param {any} value - The monetary value
 * @param {string} currencyCode - Currency code (default: 'EUR')
 * @param {string} userLanguage - User's language code
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} Formatted monetary value with currency symbol
 */
export function safeFormatMonetaryValueForUser(value, currencyCode = 'EUR', userLanguage = 'en', decimals = 2) {
    try {
        if (value === null || value === undefined || value === '' || isNaN(value)) {
            return formatCurrencyForUser(0, currencyCode, userLanguage, { 
                minimumFractionDigits: decimals, 
                maximumFractionDigits: decimals 
            });
        }

        const numValue = Number(value);
        if (!isFinite(numValue)) {
            return formatCurrencyForUser(0, currencyCode, userLanguage, { 
                minimumFractionDigits: decimals, 
                maximumFractionDigits: decimals 
            });
        }

        return formatCurrencyForUser(numValue, currencyCode, userLanguage, { 
            minimumFractionDigits: decimals, 
            maximumFractionDigits: decimals 
        });
    } catch (error) {
        console.warn('Error formatting monetary value for user:', error);
        return `${getCurrencySymbol(currencyCode)} 0,00`;
    }
}

/**
 * Validates if a value is a valid monetary amount
 * @param {any} value - The value to validate
 * @returns {boolean} True if valid monetary amount
 */
export function isValidMonetaryValue(value) {
    if (value === null || value === undefined || value === '') {
        return false;
    }
    
    const numValue = Number(value);
    return !isNaN(numValue) && isFinite(numValue);
}

/**
 * Formats percentage values according to user's locale
 * @param {number|string} value - The percentage value (e.g., 0.15 for 15%)
 * @param {string} userLanguage - User's language code
 * @param {number} decimals - Number of decimal places (default: 1)
 * @returns {string} Formatted percentage string
 */
export function formatPercentageForUser(value, userLanguage = 'en', decimals = 1) {
    if (value === null || value === undefined || value === '') {
        return '';
    }

    try {
        const numValue = Number(value);
        if (!isFinite(numValue)) {
            return '0%';
        }

        const locale = getLocaleString(userLanguage);
        
        return new Intl.NumberFormat(locale, {
            style: 'percent',
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals
        }).format(numValue);

    } catch (error) {
        console.error('Error formatting percentage for user:', error);
        return `${(Number(value) * 100).toFixed(decimals)}%`;
    }
}

/**
 * Utility function to format multiple financial fields for a user
 * @param {object} data - Object containing financial fields
 * @param {array} currencyFields - Array of field names that contain currency amounts
 * @param {array} numberFields - Array of field names that contain numbers
 * @param {string} currencyCode - Currency code to use
 * @param {string} userLanguage - User's language code
 * @returns {object} Object with formatted financial fields
 */
export function formatMultipleFinancialFieldsForUser(data, currencyFields = [], numberFields = [], currencyCode = 'EUR', userLanguage = 'en') {
    if (!data || (!currencyFields.length && !numberFields.length)) {
        return data;
    }

    const formattedData = { ...data };

    // Format currency fields
    currencyFields.forEach(field => {
        if (formattedData[field] !== undefined) {
            formattedData[field] = safeFormatMonetaryValueForUser(formattedData[field], currencyCode, userLanguage);
        }
    });

    // Format number fields
    numberFields.forEach(field => {
        if (formattedData[field] !== undefined) {
            formattedData[field] = safeFormatPriceForUser(formattedData[field], userLanguage);
        }
    });

    return formattedData;
}

/**
 * Example usage function showing how to use these utilities with user data
 * @param {object} user - User object containing language preferences
 * @param {object} financialData - Data object with financial fields to format
 * @param {string} currencyCode - Currency code to use
 * @returns {object} Formatted financial data
 */
export function formatUserSpecificFinancials(user, financialData, currencyCode = 'EUR') {
    const { language } = user;
    
    // Example formatting for common financial fields
    const formattedData = {
        ...financialData
    };

    // Format various financial fields if they exist
    if (financialData.amount) {
        formattedData.amount = safeFormatMonetaryValueForUser(financialData.amount, currencyCode, language);
    }
    
    if (financialData.totalAmount) {
        formattedData.totalAmount = safeFormatMonetaryValueForUser(financialData.totalAmount, currencyCode, language);
    }
    
    if (financialData.discount) {
        formattedData.discount = safeCalculateDiscountForUser(financialData.discount, language);
    }
    
    if (financialData.finalAmount) {
        formattedData.finalAmount = safeFormatMonetaryValueForUser(financialData.finalAmount, currencyCode, language);
    }

    return formattedData;
}